/** Round a number with a given amount of decimals */
export const round = (n: number, decimals = 0) => {
  const factor = 10 ** decimals
  return Math.round(n * factor) / factor
}

/**
 * Renvoie le pourcentage d'évolution, non arrondi : l'arrondi des variations est la
 * responsabilité de l'affichage (`formatComparisonValue`).
 * Renvoie null si previous = 0 (cas indéterminé — afficher "NC" côté UI).
 */
export const computePercentageChange = (current: number, previous: number): number | null => {
  if (previous === 0) return null
  return ((current - previous) / previous) * 100
}

/**
 * Renvoie l'écart en points entre deux pourcentages (current - previous), arrondi à une décimale.
 * Pour les taux (vol, casse...) dont l'évolution s'exprime en points et non en variation relative.
 */
export const computePointChange = (current: number, previous: number): number =>
  Math.round((current - previous) * 10) / 10

/**
 * Médiane (moyenne des deux valeurs centrales sur un nombre pair). Le tableau vide n'a pas de
 * médiane : `onEmpty` dit ce que l'appelant veut à la place (`null`, `0`, un repli), jamais `NaN`
 * par oubli. Les valeurs ne sont ni filtrées ni modifiées : écarter les non finies avant.
 */
export const median = <Empty>(values: readonly number[], onEmpty: Empty): number | Empty => {
  if (values.length === 0) return onEmpty
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle]
}

/** Moyenne arithmétique ; `onEmpty` pour le tableau vide, même règle que `median`. */
export const mean = <Empty>(values: readonly number[], onEmpty: Empty): number | Empty => {
  if (values.length === 0) return onEmpty
  let sum = 0
  for (const value of values) sum += value
  return sum / values.length
}

/** Part en pourcentage arrondie (une décimale par défaut), `null` sur un total nul. */
export const percentOf = (part: number, whole: number, decimals = 1): number | null =>
  whole > 0 ? round((part / whole) * 100, decimals) : null

// ─── Loi normale ─────────────────────────────────────────────────────────────

/** Fonction d'erreur, approximation d'Abramowitz et Stegun 7.1.26 (erreur < 1,5e-7). */
const erf = (x: number): number => {
  const sign = x < 0 ? -1 : 1
  const a = Math.abs(x)
  const t = 1 / (1 + 0.327_591_1 * a)
  const poly =
    ((((1.061_405_429 * t - 1.453_152_027) * t + 1.421_413_741) * t - 0.284_496_736) * t +
      0.254_829_592) *
    t
  return sign * (1 - poly * Math.exp(-a * a))
}

export const normalCdf = (z: number): number => 0.5 * (1 + erf(z / Math.SQRT2))

/** Quantile de la loi normale centrée réduite, par bissection sur [−6, 6]. */
export const normalQuantile = (p: number): number => {
  let lo = -6
  let hi = 6
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2
    if (normalCdf(mid) < p) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}
